<template>
  <div class="modal" :class="{ 'is-active': isActive }">
    <div class="modal-background" @click="handleCancel"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title section-title">Deactivate Banking Information</p>
        <button class="delete" aria-label="close" @click="handleCancel"></button>
      </header>
      <section class="modal-card-body">
        <div v-if="errorMessage" class="notification is-danger">
          <button class="delete" @click="errorMessage = ''"></button>
          {{ errorMessage }}
        </div>

        <p class="has-text-weight-semibold" style="margin-bottom: 1.5rem;">
          Are you sure you want to deactivate this banking information?
        </p>

        <div class="box" style="background-color: #f9f9f9;">
          <h4 class="title is-5">Banking Information Details</h4>
          <div class="content">
            <p><strong>Business Name:</strong> {{ payment.po_business_name || 'N/A' }}</p>
            <p><strong>Bank Name:</strong> {{ payment.bank_name || 'N/A' }}</p>
            <p><strong>Name on Account:</strong> {{ payment.name_on_account || 'N/A' }}</p>
            <p><strong>Account Number:</strong> ****{{ payment.last_four_account_number || 'N/A' }}</p>
          </div>
        </div>

      </section>
      <footer class="modal-card-foot">
        <div class="buttons">
          <button
            class="button is-danger"
            @click="handleConfirm"
            :class="{ 'is-loading': isLoading }"
            :disabled="isLoading"
          >
            <span class="icon">
              <i class="fas fa-trash"></i>
            </span>
            <span>Deactivate Banking Information</span>
          </button>
          <button
            class="button is-secondary"
            @click="handleCancel"
            :disabled="isLoading"
          >
            Cancel
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'DeactivatePaymentModal',
  props: {
    isActive: {
      type: Boolean,
      required: true,
    },
    payment: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoading: false,
      errorMessage: '',
    };
  },
  computed: {
    ...mapGetters('auth', ['userInfo']),
  },
  methods: {
    handleCancel() {
      if (!this.isLoading) {
        this.errorMessage = '';
        this.$emit('close');
      }
    },
    async handleConfirm() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        // Prepare the full payment data with deactive_account flag
        const paymentData = {
          rid: this.payment.rid,
          deactive_account: true,
          // Include all existing payment data to avoid validation errors
          account_number: this.payment.account_number,
          routing_number: this.payment.routing_number,
          name_on_account: this.payment.name_on_account,
          bank_name: this.payment.bank_name,
          po_business_name: this.payment.po_business_name,
          w9_address_street_1: this.payment.w9_address_street_1,
          w9_address_street_2: this.payment.w9_address_street_2,
          w9_address_city: this.payment.w9_address_city,
          w9_address_state: this.payment.w9_address_state,
          w9_address_postal_code: this.payment.w9_address_postal_code,
          w9_address_country: this.payment.w9_address_country || 'US',
          tax_id_type: this.payment.tax_id_type,
          ssn_number: this.payment.ssn_number,
          ein_number: this.payment.ein_number,
        };

        // Use existing submitPayment action
        const result = await this.$store.dispatch('dashboard/submitPayment', {
          organization_rid: this.userInfo.qbUser.related_organization,
          paymentData: paymentData,
        });

        if (!result.success) {
          this.errorMessage = result.message || 'Failed to deactivate payment method';
          this.isLoading = false;
          return;
        }

        // Update the payment status in the local state since backend doesn't return it
        this.$store.commit('dashboard/updatePaymentStatus', {
          rid: this.payment.rid,
          status: 'Deactivated',
        });

        // Success! Emit success and reset
        this.$emit('confirm', this.payment);
        this.resetModal();
      } catch (error) {
        console.error('Error deactivating payment:', error);
        this.errorMessage = 'An unexpected error occurred. Please try again.';
        this.isLoading = false;
      }
    },
    resetModal() {
      this.isLoading = false;
      this.errorMessage = '';
    },
  },
  watch: {
    isActive(newVal) {
      if (!newVal) {
        this.resetModal();
      }
    },
  },
};
</script>

<style scoped>
.modal {
  z-index: 1003;
}

.modal-background {
  z-index: 1003;
}

.modal-card {
  z-index: 1004;
  max-width: 600px;
  width: 90%;
}

.modal-card-head {
  padding: 0.5rem;
}

.modal-card-body {
  padding: 0.5rem;
}

.modal-card-foot {
  padding: 1rem;
}

.section-title {
  font-size: 1.25rem;
  font-family: 'Montserrat', sans-serif;
  color: #000000;
  margin-bottom: 0;
}

.notification.is-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.box {
  margin-bottom: 1rem;
}

.buttons {
  width: 100%;
  justify-content: flex-start;
}
</style>
